import { useMutation } from '@apollo/client'
import TabList from '@mui/lab/TabList'
import { Box, Chip, Link, Tab, Typography } from '@mui/material'
import { type GetCandidateProfileForRecruiterQuery } from '__generated__/graphql'
import type { Dispatch, FC, SetStateAction } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import { formatPhoneNumber } from 'react-phone-number-input'
import CandidateAvatar from 'recruiter/components/CandidateAvatar'
import {
    outreachStatusIcons,
    recruitingStatusIcons,
    systemStatusIcons,
} from 'shared/components/candidateStatusIcons'
import CandidateRatingDisplay from 'shared/components/CandidateRatingDisplay'
import useToast from 'shared/components/Toast/useToast'
import { isCandidateRecruitingStatus } from 'shared/models/candidateRecruitingStatus'
import { getOutreachStatusLabel, getSystemStatusLabel } from 'shared/utils'
import { ResendPortalInvitation } from 'recruiter/pages/Rosters/RosterProfile/BulkActions/BulkActions.gql'
import ActionsMenu from './ActionsMenu'
import CandidateDataPoints from './CandidateDataPoints'
import { EmailLink } from './styled'

interface ProfileHeaderProps {
    data: GetCandidateProfileForRecruiterQuery
    setEdit: Dispatch<SetStateAction<boolean>>
    activeTab: string
    candidateId: string
}

const ProfileHeader: FC<ProfileHeaderProps> = ({
    data,
    setEdit,
    activeTab,
    candidateId,
}) => {
    const toast = useToast()

    const [resendEmail] = useMutation(ResendPortalInvitation)
    const currentWorkExperience = data.candidate?.workExperience?.find(
        (workExperience) => workExperience.isCurrent
    )
    const workEndDate = currentWorkExperience?.endDate
    // only hide work email/phone if student
    const workEmail = currentWorkExperience?.workEmail
    const workPhone = currentWorkExperience?.workPhone
    const shouldShowWorkEmail =
        !currentWorkExperience?.title?.isStudent && workEmail && !workEndDate
    const shouldShowWorkPhone =
        !currentWorkExperience?.title?.isStudent && workPhone && !workEndDate

    const resumeId =
        data.candidate?.documents?.find((doc) => doc.documentType === 'Resume')
            ?.id || ''
    const profileCompletion = Math.floor(
        ((
            data.candidate?.candidateIntakes?.filter(
                (intake) => !!intake.completionDate
            ) ?? []
        ).length /
            (data.candidate?.candidateIntakes ?? []).length) *
            100 || 0
    )
    const resendRegistrationEmail = async () => {
        try {
            const { data: mutationResponse } = await resendEmail({
                variables: {
                    userIds: data.candidate?.user?.id ?? [],
                    role: 'candidate',
                },
            })
            if (mutationResponse?.resendPortalInvitationEmail?.success) {
                toast({
                    title: 'Success',
                    message: 'Sent the portal registration email',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Error',
                    message: 'Failed to send registration email',
                    severity: 'error',
                })
            }
        } catch (error) {
            toast({
                title: 'Error',
                message: 'Failed to send registration email',
                severity: 'error',
            })
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                p: 2,
                width: '100%',
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}
        >
            <CandidateAvatar
                firstName={
                    data.candidate?.user?.nickname ??
                    data.candidate?.user?.firstName
                }
                lastName={data.candidate?.user?.lastName}
                profileImage={
                    data.candidate?.user?.profileImageKey ||
                    data.candidate?.internalProfileImageKey ||
                    ''
                }
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexShrink: 0,
                    width: 100,
                    height: 100,
                    fontSize: '2.5rem',
                }}
            />

            <Box
                sx={{
                    flex: 1,
                    minWidth: 280,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                        marginBottom: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'baseline',
                        }}
                    >
                        <CandidateAvatar
                            firstName={
                                data.candidate?.user?.nickname ??
                                data.candidate?.user?.firstName
                            }
                            lastName={data.candidate?.user?.lastName}
                            profileImage={
                                data.candidate?.user?.profileImageKey ||
                                data.candidate?.internalProfileImageKey ||
                                ''
                            }
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                width: 32,
                                height: 32,
                                fontSize: '0.875rem',
                            }}
                        />
                        <Typography
                            variant="h2"
                            sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontWeight: 500,
                            }}
                        >
                            {data.candidate?.user?.nickname ??
                                data.candidate?.user?.firstName}{' '}
                            {data.candidate?.user?.lastName}
                        </Typography>
                        {(() => {
                            const chipSx = {
                                borderColor: '#007BC7',
                                color: '#007BC7',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: 26,
                                pl: 0.75,
                            }
                            const s = data.candidate?.recruitingStatus?.status
                            const recStatus =
                                s && isCandidateRecruitingStatus(s) ? s : null
                            const RecIcon = recStatus
                                ? recruitingStatusIcons[recStatus] ?? null
                                : null

                            const rawOut =
                                data.candidate?.outreachStatus?.status
                            const outLabel = rawOut
                                ? getOutreachStatusLabel(rawOut)
                                : null
                            const OutIcon = outLabel
                                ? outreachStatusIcons[outLabel] ?? null
                                : null

                            const rawSys = data.candidate?.systemStatus?.status
                            const sysLabel = rawSys
                                ? getSystemStatusLabel(rawSys)
                                : null
                            const SysIcon = sysLabel
                                ? systemStatusIcons[sysLabel] ?? null
                                : null

                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        ml: 1,
                                        alignItems: 'center',
                                    }}
                                >
                                    {RecIcon && recStatus && (
                                        <Chip
                                            variant="outlined"
                                            size="small"
                                            icon={<RecIcon />}
                                            label={recStatus}
                                            sx={chipSx}
                                        />
                                    )}
                                    {OutIcon && outLabel && (
                                        <Chip
                                            variant="outlined"
                                            size="small"
                                            icon={<OutIcon />}
                                            label={outLabel}
                                            sx={chipSx}
                                        />
                                    )}
                                    {SysIcon && sysLabel && (
                                        <Chip
                                            variant="outlined"
                                            size="small"
                                            icon={<SysIcon />}
                                            label={sysLabel}
                                            sx={chipSx}
                                        />
                                    )}
                                </Box>
                            )
                        })()}
                    </Box>
                    <ActionsMenu
                        name={`${data.candidate?.user?.nickname ?? data.candidate?.user?.firstName} ${data.candidate?.user?.lastName}`}
                        profileCompletion={profileCompletion}
                        linkedinUrl={data.candidate?.linkedinUrl ?? ''}
                        email={data.candidate?.user?.email ?? ''}
                        phone={data.candidate?.user?.phone ?? ''}
                        handleEditClick={() => setEdit(true)}
                        handleResendRegistrationClick={() => {
                            resendRegistrationEmail()
                        }}
                        resumeId={resumeId}
                        lastCandidateLogin={data.candidate?.lastLogin}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        marginBottom: 1.5,
                    }}
                >
                    {data.candidate && (
                        <CandidateRatingDisplay
                            candidate={data.candidate}
                            showNumber
                            showCount
                            showDetails
                        />
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {'Last Login: '}
                        {data.candidate?.lastLogin
                            ? `${new Date(data.candidate?.lastLogin).toLocaleDateString()} • ${new Date(data.candidate?.lastLogin).toLocaleTimeString()}`
                            : '-'}
                    </Typography>
                </Box>

                <CandidateDataPoints data={data} />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <div>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Preferred Email
                            </Typography>
                            <EmailLink
                                href={`mailto:${data.candidate?.preferredEmail}`}
                                title={data.candidate?.preferredEmail ?? ''}
                            >
                                {data.candidate?.preferredEmail}
                            </EmailLink>
                        </div>
                        <div>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Personal Email
                            </Typography>
                            <EmailLink
                                href={`mailto:${data.candidate?.user?.email}`}
                                title={data.candidate?.user?.email ?? ''}
                            >
                                {data.candidate?.user?.email}
                            </EmailLink>
                        </div>
                        {shouldShowWorkEmail && (
                            <div>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Work Email
                                </Typography>
                                <EmailLink
                                    href={`mailto:${workEmail}`}
                                    title={workEmail}
                                >
                                    {workEmail}
                                </EmailLink>
                            </div>
                        )}
                        <div>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Personal Cell Phone
                            </Typography>
                            <Link
                                underline="none"
                                color="other.secondary_light"
                                variant="body2"
                                href={`tel:${data.candidate?.user?.phone}`}
                                sx={{ display: 'block' }}
                            >
                                {formatPhoneNumber(
                                    data.candidate?.user?.phone ?? ''
                                )}
                            </Link>
                        </div>
                        {shouldShowWorkPhone && (
                            <div>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Work Cell Phone
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="other.secondary_light"
                                >
                                    {workPhone}
                                </Typography>
                            </div>
                        )}
                    </Box>
                    <TabList
                        sx={{
                            flexShrink: 0,
                            minHeight: 'auto',
                            '& .MuiTabs-flexContainer': {
                                gap: 0.5,
                            },
                            '& .MuiTab-root': {
                                minHeight: 'auto',
                                minWidth: 'auto',
                                py: 1,
                                px: 1.5,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                            },
                        }}
                    >
                        <Tab
                            label="Overview"
                            value="overview"
                            component={RouterLink}
                            to={`${recruiterRouteConfig.candidates.path}/${candidateId}/overview/on`}
                        />
                        <Tab
                            label="Career"
                            value="career"
                            component={RouterLink}
                            to={`${recruiterRouteConfig.candidates.path}/${candidateId}/career`}
                        />
                        <Tab
                            label="Background"
                            value="background"
                            component={RouterLink}
                            to={`${recruiterRouteConfig.candidates.path}/${candidateId}/background`}
                        />
                        <Tab
                            label="Communications"
                            value="communications"
                            component={RouterLink}
                            to={`${recruiterRouteConfig.candidates.path}/${candidateId}/communications`}
                        />
                        <Tab
                            label="Documents"
                            value="documents"
                            component={RouterLink}
                            to={`${recruiterRouteConfig.candidates.path}/${candidateId}/documents`}
                        />
                    </TabList>
                </Box>
            </Box>
        </Box>
    )
}

export default ProfileHeader
